import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';

interface SelectionContextType<T> {
  selectedIds: Set<string | number>;
  toggle: (id: string | number) => void;
  toggleAll: (items: T[]) => void;
  isAllSelected: (items: T[]) => boolean;
  isIndeterminate: (items: T[]) => boolean;
  clearSelection: () => void;
  selectedCount: number;
  setSelected: (ids: Set<string | number>, items?: T[]) => void;
}

const SelectionContext = createContext<SelectionContextType<any> | undefined>(
  undefined,
);

interface SelectionProviderProps<T> {
  children: ReactNode;
  getRowId: (item: T) => string | number;
  getIsDisabled?: (item: T, index: number) => boolean;
}

export function SelectionProvider<T>({
  children,
  getRowId,
  getIsDisabled,
}: SelectionProviderProps<T>) {
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(
    new Set(),
  );

  const toggle = useCallback((id: string | number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const getSelectableIds = useCallback(
    (items: T[]) => {
      return items
        .filter((item, index) => !getIsDisabled?.(item, index))
        .map(getRowId);
    },
    [getRowId, getIsDisabled],
  );

  const toggleAll = useCallback(
    (items: T[]) => {
      setSelectedIds(prev => {
        const selectableIds = getSelectableIds(items);
        const allSelectableSelected = selectableIds.every(id => prev.has(id));

        const next = new Set(prev);
        if (allSelectableSelected) {
          selectableIds.forEach(id => next.delete(id));
        } else {
          selectableIds.forEach(id => next.add(id));
        }
        return next;
      });
    },
    [getSelectableIds],
  );

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const isAllSelected = useCallback(
    (items: T[]) => {
      const selectableIds = getSelectableIds(items);
      if (selectableIds.length === 0) return false;
      return selectableIds.every(id => selectedIds.has(id));
    },
    [selectedIds, getSelectableIds],
  );

  const isIndeterminate = useCallback(
    (items: T[]) => {
      const selectableIds = getSelectableIds(items);
      if (selectableIds.length === 0) return false;

      const selectedInView = selectableIds.filter(id =>
        selectedIds.has(id),
      ).length;
      return selectedInView > 0 && selectedInView < selectableIds.length;
    },
    [selectedIds, getSelectableIds],
  );

  const setSelected = useCallback(
    (ids: Set<string | number>, items?: T[]) => {
      if (items) {
        const selectableIds = new Set(getSelectableIds(items));
        const validIds = Array.from(ids).filter(id => selectableIds.has(id));
        setSelectedIds(new Set(validIds));
      } else {
        setSelectedIds(new Set(ids));
      }
    },
    [getSelectableIds],
  );

  const value = useMemo(
    () => ({
      selectedIds,
      toggle,
      toggleAll,
      isAllSelected,
      isIndeterminate,
      clearSelection,
      selectedCount: selectedIds.size,
      setSelected,
    }),
    [
      selectedIds,
      toggle,
      toggleAll,
      isAllSelected,
      isIndeterminate,
      clearSelection,
      setSelected,
    ],
  );

  return (
    <SelectionContext.Provider value={value}>
      {children}
    </SelectionContext.Provider>
  );
}

export function useSelectionContext<T>() {
  const context = useContext(SelectionContext);
  if (!context) {
    throw new Error(
      'useSelectionContext must be used within a SelectionProvider',
    );
  }
  return context as SelectionContextType<T>;
}
